import React, { FC, ReactNode, useCallback } from 'react'
import { Box, Button, Chip, Stack, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import {
  ArrowBack as ArrowBackIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Banner,
  PageLoading,
  PageScaffold,
  SubsectionHeader,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { useMetricsDetailQuery } from '@/features/internal/metrics'
import {
  MetricsAddition,
  MetricsMedication,
} from '@/features/internal/metrics/types/metrics'

interface FieldProps {
  label: string
  children: ReactNode
}

const Field: FC<FieldProps> = ({ label, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={PORTAL_TEXT.cellMeta}>{label}</Typography>
    <Box sx={{ mt: 0.25 }}>{children}</Box>
  </Box>
)

const Value: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography sx={PORTAL_TEXT.cellBody}>{children}</Typography>
)

export const MetricsDetailPage: FC = () => {
  const navigate = useNavigate()
  const { id = '' } = useParams<{ id: string }>()

  const {
    data: metric,
    isLoading: loading,
    error: queryError,
  } = useMetricsDetailQuery({ id })
  const error = queryError?.message ?? null

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${date.getFullYear()}年${
      date.getMonth() + 1
    }月${date.getDate()}日 ${date.getHours()}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`
  }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${seconds}秒 (${minutes}分${remainingSeconds}秒)`
  }

  const calculateTimePerMedication = useCallback(
    (audioSeconds: number, medicationCount: number) => {
      if (!audioSeconds || !medicationCount) return '-'
      return `${Math.round(audioSeconds / medicationCount)}秒/剤`
    },
    []
  )

  const groupMedicationsByRP = useCallback(
    (medications: MetricsMedication[]) => {
      return medications.reduce((acc, med) => {
        if (!acc[med.rp_number]) {
          acc[med.rp_number] = []
        }
        acc[med.rp_number].push(med)
        return acc
      }, {} as Record<string, MetricsMedication[]>)
    },
    []
  )

  const handleViewOriginalData = () => {
    if (metric?.yakureki) {
      navigate(`/soap/${metric.yakureki}`)
    }
  }

  if (!metric && !error) {
    return null
  }

  return (
    <PageScaffold
      title="指導実績詳細"
      actions={
        <Button
          variant="outlined"
          size="small"
          color="inherit"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/portal/metrics/records')}
        >
          一覧に戻る
        </Button>
      }
    >
      {loading && <PageLoading />}
      {error && <Banner severity="danger">{error}</Banner>}

      {metric && (
        <Stack gap={4}>
          <Box>
            <SubsectionHeader heading="基本情報" />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field label="作成日時">
                  <Value>{formatDateTime(metric.created_at)}</Value>
                </Field>
                <Field label="処方箋番号">
                  <Value>{metric.prescription_number}</Value>
                </Field>
                <Field label="医療機関名">
                  <Value>{metric.medical_institution_name || '-'}</Value>
                </Field>
                {metric.department_name && (
                  <Field label="診療科名">
                    <Value>{metric.department_name}</Value>
                  </Field>
                )}
                {metric.doctor_name && (
                  <Field label="処方医師名">
                    <Value>{metric.doctor_name}</Value>
                  </Field>
                )}
                {metric.pharmacy_name && (
                  <Field label="薬局名">
                    <Value>{metric.pharmacy_name}</Value>
                  </Field>
                )}
                {metric.pharmacist_name && (
                  <Field label="薬剤師名">
                    <Value>{metric.pharmacist_name}</Value>
                  </Field>
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Field label="薬剤数">
                  <Value>{metric.medication_count}剤</Value>
                </Field>
                <Field label="最終更新">
                  <Value>{formatDateTime(metric.updated_at)}</Value>
                </Field>
                {metric.prescription_date && (
                  <Field label="処方箋交付日">
                    <Value>{metric.prescription_date}</Value>
                  </Field>
                )}
                {metric.dispensing_date && (
                  <Field label="調剤日">
                    <Value>{metric.dispensing_date}</Value>
                  </Field>
                )}
                <Field label="元データ">
                  <Button
                    variant="outlined"
                    size="small"
                    color="inherit"
                    startIcon={<LaunchIcon />}
                    onClick={handleViewOriginalData}
                  >
                    薬歴画面を表示
                  </Button>
                </Field>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <SubsectionHeader heading="音声・会話情報" />
            <Field label="音声録音">
              <Chip
                label={metric.has_audio ? 'あり' : 'なし'}
                size="small"
                sx={{
                  bgcolor: colorTokens.bg.subtle,
                  color: metric.has_audio
                    ? colorTokens.accent.primary
                    : colorTokens.fg.muted,
                  border: `1px solid ${colorTokens.border.default}`,
                }}
              />
            </Field>
            {metric.has_audio && (
              <>
                <Field label="指導時間">
                  <Value>{formatDuration(metric.audio_duration_seconds)}</Value>
                </Field>
                <Field label="薬剤あたり指導時間">
                  <Value>
                    {calculateTimePerMedication(
                      metric.audio_duration_seconds,
                      metric.medication_count
                    )}
                  </Value>
                </Field>
                <Field label="やりとり回数">
                  <Value>{metric.conversation_exchange_count}回</Value>
                </Field>
                {metric.speech_rate !== undefined && (
                  <Field label="話速">
                    <Value>
                      {metric.speech_rate
                        ? `${metric.speech_rate}文字/分`
                        : '-'}
                    </Value>
                  </Field>
                )}
              </>
            )}
          </Box>

          <Box>
            <SubsectionHeader heading="SOAP情報" />
            <Field label="SOAP生成">
              <Chip
                label={metric.has_soap ? '済み' : '未生成'}
                size="small"
                sx={{
                  bgcolor: colorTokens.bg.subtle,
                  color: metric.has_soap
                    ? colorTokens.accent.primary
                    : colorTokens.fg.muted,
                  border: `1px solid ${colorTokens.border.default}`,
                }}
              />
            </Field>
            <Field label="SOAPコピー">
              <Chip
                label={metric.soap_copied ? '済み' : '未実行'}
                size="small"
                sx={{
                  bgcolor: colorTokens.bg.subtle,
                  color: metric.soap_copied
                    ? colorTokens.accent.primary
                    : colorTokens.fg.muted,
                  border: `1px solid ${colorTokens.border.default}`,
                }}
              />
            </Field>
          </Box>

          <Box>
            <SubsectionHeader
              heading={`加算情報 (${metric.additions.length}件)`}
            />
            {metric.additions.length > 0 ? (
              <Grid container spacing={1}>
                {metric.additions.map(
                  (addition: MetricsAddition, index: number) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                      <Stack
                        gap={0.25}
                        sx={{
                          py: 1,
                          px: 1.5,
                          borderBottom: `1px solid ${colorTokens.border.default}`,
                        }}
                      >
                        <Typography sx={PORTAL_TEXT.cellBody}>
                          {addition.name}
                        </Typography>
                        <Typography sx={PORTAL_TEXT.cellMeta}>
                          {addition.code}
                        </Typography>
                      </Stack>
                    </Grid>
                  )
                )}
              </Grid>
            ) : (
              <Typography sx={PORTAL_TEXT.cellMeta}>
                加算情報はありません
              </Typography>
            )}
          </Box>

          {metric.medications && metric.medications.length > 0 && (
            <Box>
              <SubsectionHeader heading="処方薬剤情報" />
              {(() => {
                const groupedMedications = groupMedicationsByRP(
                  metric.medications
                )
                const rpEntries = Object.entries(groupedMedications)
                return rpEntries.map(([rpNumber, meds], rpIndex) => (
                  <Box
                    key={rpNumber}
                    sx={{
                      pb: 1.5,
                      mb: rpIndex < rpEntries.length - 1 ? 1.5 : 0,
                      borderBottom:
                        rpIndex < rpEntries.length - 1
                          ? `1px solid ${colorTokens.border.default}`
                          : 'none',
                    }}
                  >
                    <Typography
                      sx={{
                        ...PORTAL_TEXT.cellSecondary,
                        color: colorTokens.accent.primary,
                        mb: 1,
                      }}
                    >
                      RP {rpNumber}
                    </Typography>

                    {meds.map((med, index) => (
                      <Box key={index} sx={{ mb: 0.5 }}>
                        <Stack direction="row" alignItems="baseline" gap={2}>
                          <Typography sx={PORTAL_TEXT.cellBody}>
                            {med.name}
                          </Typography>
                          <Typography
                            sx={{ ...PORTAL_TEXT.cellBody, ml: 'auto' }}
                          >
                            {med.dosage} {med.unit}
                          </Typography>
                        </Stack>
                        {med.drug_comment && (
                          <Typography sx={{ ...PORTAL_TEXT.cellMeta, ml: 2 }}>
                            薬品コメント: {med.drug_comment}
                          </Typography>
                        )}
                      </Box>
                    ))}

                    {meds[0].usage && (
                      <Box sx={{ mt: 1, ml: 2 }}>
                        <Typography sx={PORTAL_TEXT.cellBody}>
                          用法: {meds[0].usage}
                        </Typography>
                        {meds[0].usage_comment && (
                          <Typography sx={PORTAL_TEXT.cellMeta}>
                            用法コメント: {meds[0].usage_comment}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                ))
              })()}
            </Box>
          )}
        </Stack>
      )}
    </PageScaffold>
  )
}
